import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { usePartners, useCreateDividendDistribution } from '../api/useCosts';
import { formatUsd, pnlColor } from '../utils/format';

interface LineDraft {
  key: number;
  partnerId: string;
  amount: string;
}

const emptyLine = (key: number): LineDraft => ({
  key,
  partnerId: '',
  amount: '',
});

// Distribute the period's profit across partners and close the month. The
// current month's P&L is shown so the amounts can be sized against it;
// submitting freezes the Real P&L snapshot that becomes next month's baseline.
export const RecordDistributionDialog = ({
  open,
  onClose,
  currentMonthPnl,
}: {
  open: boolean;
  onClose: () => void;
  currentMonthPnl: number | undefined;
}) => {
  const { data: partners } = usePartners();
  const { mutate: create, isLoading } = useCreateDividendDistribution();
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(0)]);
  const [nextKey, setNextKey] = useState(1);
  const [snapshotOnly, setSnapshotOnly] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [manualOverride, setManualOverride] = useState(false);
  const [manualPnl, setManualPnl] = useState('');

  const activePartners = useMemo(
    () => (partners ?? []).filter(p => p.active),
    [partners]
  );

  const pickedPartnerIds = useMemo(
    () => new Set(lines.map(l => l.partnerId).filter(Boolean)),
    [lines]
  );

  const total = useMemo(
    () =>
      lines.reduce((acc, l) => {
        const num = parseFloat(l.amount);
        return isNaN(num) ? acc : acc + num;
      }, 0),
    [lines]
  );

  const canSubmit = useMemo(() => {
    if (manualOverride && isNaN(parseFloat(manualPnl))) return false;
    if (snapshotOnly) return true;
    if (lines.length === 0) return false;
    return lines.every(l => {
      const num = parseFloat(l.amount);
      return l.partnerId !== '' && !isNaN(num) && num > 0;
    });
  }, [lines, manualOverride, manualPnl, snapshotOnly]);

  const updateLine = (key: number, patch: Partial<LineDraft>) =>
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const addLine = () => {
    setLines(prev => [...prev, emptyLine(nextKey)]);
    setNextKey(k => k + 1);
  };

  const removeLine = (key: number) =>
    setLines(prev => prev.filter(l => l.key !== key));

  const handleSubmit = () => {
    if (!canSubmit) return;
    create(
      {
        // A period close that distributes nothing is legal, but an accidentally
        // empty `lines` payload must never silently freeze the snapshot chain —
        // hence the explicit flag instead of treating "empty" as snapshot-only.
        lines: snapshotOnly
          ? undefined
          : lines.map(l => ({
              partnerId: l.partnerId,
              amountUsd: parseFloat(l.amount),
            })),
        snapshotOnly: snapshotOnly ? true : undefined,
        settledAt: new Date(date).toISOString(),
        note: note.trim() || undefined,
        monthPnlUsd: manualOverride ? parseFloat(manualPnl) : undefined,
      },
      {
        onSuccess: () => {
          setLines([emptyLine(0)]);
          setNextKey(1);
          setSnapshotOnly(false);
          setNote('');
          setManualOverride(false);
          setManualPnl('');
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem' }}>
        Record Dividend / Close Month
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
          Registrar un dividendo no mueve plata. Si retiraste de un exchange
          para pagarlo, cargá el retiro en Deposits &amp; Withdrawals.
        </Alert>
        <Typography sx={{ fontSize: '0.8rem', color: 'grey.500', mb: 1 }}>
          Current month P&amp;L so far:{' '}
          <Box
            component="span"
            sx={{ color: pnlColor(currentMonthPnl), fontWeight: 600 }}
          >
            {formatUsd(currentMonthPnl)}
          </Box>
        </Typography>
        <FormControlLabel
          sx={{ mb: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={manualOverride}
              onChange={e => {
                const on = e.target.checked;
                setManualOverride(on);
                // Prefill with the live figure so the operator only tweaks it.
                if (on && manualPnl === '' && currentMonthPnl != null) {
                  setManualPnl(String(currentMonthPnl));
                }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.8rem' }}>
              Enter month P&amp;L manually
            </Typography>
          }
        />
        <FormControlLabel
          sx={{ display: 'block', mb: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={snapshotOnly}
              onChange={e => setSnapshotOnly(e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.8rem' }}>
              Solo cerrar período (sin dividendo)
            </Typography>
          }
        />
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {manualOverride && (
            <TextField
              size="small"
              label="Month P&L (USD)"
              type="number"
              value={manualPnl}
              onChange={e => setManualPnl(e.target.value)}
              fullWidth
              helperText="Overrides the live computed P&L for this month's close. May be negative."
            />
          )}
          {!snapshotOnly && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  textTransform: 'uppercase',
                  color: 'grey.600',
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Partners
              </Typography>
              <Stack spacing={1.5}>
                {lines.map(line => (
                  <Stack
                    key={line.key}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <TextField
                      size="small"
                      select
                      label="Partner"
                      value={line.partnerId}
                      onChange={e =>
                        updateLine(line.key, { partnerId: e.target.value })
                      }
                      sx={{ flex: 1 }}
                    >
                      {activePartners.map(partner => (
                        <MenuItem
                          key={partner._id}
                          value={partner._id}
                          disabled={
                            partner._id !== line.partnerId &&
                            pickedPartnerIds.has(partner._id)
                          }
                        >
                          {partner.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Amount (USD)"
                      type="number"
                      value={line.amount}
                      onChange={e =>
                        updateLine(line.key, { amount: e.target.value })
                      }
                      sx={{ width: 150 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeLine(line.key)}
                      disabled={lines.length === 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                <Button size="small" onClick={addLine}>
                  + Add partner
                </Button>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  Total: {formatUsd(total)}
                </Typography>
              </Stack>
            </Box>
          )}
          <TextField
            size="small"
            label="Date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            label="Note"
            value={note}
            onChange={e => setNote(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSubmit || isLoading}
          onClick={handleSubmit}
        >
          Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};
